import React, { useEffect, useState } from "react";
import { Landmark, AlertTriangle, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useApp, formatBRL, formatDate } from "../context/AppContext";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

export const isFaturaRescisao = (f) =>
  !!f && ((f.manual || []).some((i) => i.rescisao) || /fatura de rescis/i.test(f.observacao || ""));

const maisDias = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

export const RescisaoAprovarDialog = ({ fatura, residente, open, onClose, onEmitido }) => {
  const { gerarBoletoFatura } = useApp();
  const [venc, setVenc] = useState(maisDias(10));
  const [enviar, setEnviar] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setVenc(maisDias(10)); setEnviar(true); } }, [open]);
  if (!fatura) return null;

  const itens = [...(fatura.extras || []), ...(fatura.manual || []),
    ...(fatura.itens || []).map((c) => ({ id: c.id, descricao: `Consumo ${formatDate(c.data)} · ${c.qtdEmpresa} un`, valor: c.valorCobrado }))];

  const aprovar = async () => {
    setBusy(true);
    try {
      const r = await gerarBoletoFatura(fatura.residenteId, fatura.mes, { vencimento: venc, enviar });
      const sandbox = /sandbox/i.test(`${r.bankSlipUrl || ""}${r.invoiceUrl || ""}`);
      const partes = [`Boleto de rescisão emitido (venc. ${formatDate(r.vencimento)})`];
      if (r.email?.status === "enviado") partes.push(`e-mail p/ ${r.email.para}`);
      if (r.whatsapp?.boleto === "enviado" || r.whatsapp?.fatura === "enviado") partes.push("WhatsApp enviado");
      if (sandbox) toast.warning("Boleto criado em AMBIENTE DE TESTE (Sandbox) do Asaas.");
      else toast.success(partes.join(" · "));
      onEmitido && onEmitido(r);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao emitir o boleto de rescisão.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-lg" data-testid="rescisao-aprovar-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Landmark className="w-5 h-5 text-blue-600" /> Aprovar fatura de rescisão</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <p className="text-slate-600">
            <b>{residente?.nome}</b> · Ref. {fatura.mes}
            {residente?.dataInativacao && <> · inativado em {formatDate(residente.dataInativacao)}</>}
          </p>
          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <table className="w-full text-sm"><tbody className="divide-y divide-slate-100">
              {itens.map((it, i) => (
                <tr key={it.id || i} data-testid={`rescisao-item-${i}`}>
                  <td className="px-3 py-2 text-slate-700">{it.descricao}</td>
                  <td className="px-3 py-2 text-right font-medium text-slate-700 whitespace-nowrap">{formatBRL(it.valor)}</td>
                </tr>
              ))}
              <tr className="bg-slate-50">
                <td className="px-3 py-2 font-semibold text-slate-800">Total</td>
                <td className="px-3 py-2 text-right font-bold text-slate-900" data-testid="rescisao-total">{formatBRL(fatura.total)}</td>
              </tr>
            </tbody></table>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Vencimento do boleto</label>
            <input type="date" value={venc} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setVenc(e.target.value)}
              className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="rescisao-vencimento" />
            <p className="text-[11px] text-slate-400 mt-1">Sugestão: 10 dias a partir de hoje.</p>
          </div>
          <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" checked={enviar} onChange={(e) => setEnviar(e.target.checked)} className="mt-0.5" data-testid="rescisao-enviar" />
            <span>Enviar boleto + fatura ao responsável financeiro (WhatsApp / E-mail, conforme os interruptores de Integrações).</span>
          </label>
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> Ao aprovar, a cobrança é criada no Asaas (produção) com o valor acima.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="rescisao-cancelar">Cancelar</Button>
          <Button className="bg-blue-600 hover:bg-blue-700 gap-2" onClick={aprovar} disabled={busy || !venc || !(fatura.total > 0)} data-testid="rescisao-aprovar-emitir">
            <Landmark className="w-4 h-4" /> {busy ? "Emitindo..." : "Aprovar e emitir boleto"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const BoletoLink = ({ bol, testid }) => {
  const url = bol?.invoiceUrl || bol?.bankSlipUrl;
  if (!url) return null;
  return (
    <a href={url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline inline-flex items-center gap-1" data-testid={testid}>
      <ExternalLink className="w-3.5 h-3.5" /> Ver boleto
    </a>
  );
};
